import { useRef } from "react";
import { motion, useScroll } from "framer-motion";
import SEO from "../components/SEO";
import { Reveal, Eyebrow } from "../components/Reveal";

const MILESTONES = [
  {
    year: "1976",
    title: "The Beginning",
    points: [
      "The Kapoor family begins its business journey in jewellery.",
      "Operations begin from Sarafa Bazaar, Meerut.",
      "The foundation is built on trust, relationships, craftsmanship and knowledge of the jewellery trade.",
    ],
    photo: null,
    image: "/images/story-1976.jpg",
    caption: "The goldsmith's bench, Sarafa Bazaar — illustrated",
  },
  {
    year: "1991",
    title: "The Journey Expands",
    points: [
      "The family enters the real estate sector.",
      "RK Builders begins its journey.",
      "The business grows into land development, planned residential societies, plotted developments, villas and organised communities.",
    ],
    photo: null,
    image: "/images/story-1991.jpg",
    caption: "Surveying the first development land — illustrated",
  },
  {
    year: "1990s–2000s",
    title: "Building Across Generations",
    points: [
      "The jewellery business continues strengthening its manufacturing and wholesale operations.",
      "RK Builders expands through residential societies and developments.",
      "Both businesses continue growing around the shared values of trust, quality, relationships and long-term value.",
    ],
    photo: null,
    image: null,
  },
  {
    year: "2014",
    title: "Expanding to Delhi",
    points: [
      "A dedicated wholesale office in Karol Bagh, Delhi is established.",
      "The expansion strengthens the jewellery company's wholesale presence and business relationships beyond Meerut.",
    ],
    photo: null,
    image: "/images/story-2014.jpg",
    caption: "The Karol Bagh wholesale desk — illustrated",
  },
  {
    year: "2016",
    title: "A New Retail Experience",
    points: [
      "The Abu Plaza retail showroom in Meerut opens.",
      "It establishes a dedicated premium retail presence while wholesale remains the principal strength of the jewellery business.",
    ],
    photo: null,
    image: "/images/showroom.jpg",
    caption: "The Abu Plaza retail showroom — illustrated",
  },
  {
    year: "Today",
    title: "One Group. Two Established Businesses.",
    points: [
      "The businesses come together under the parent identity of RK Group.",
      "Ramesh Kapoor and Sons Jewellers Pvt. Ltd. operates across jewellery manufacturing, wholesale and retail.",
      "RK Builders represents the Group's real estate development business.",
      "RK Group is led by Mr. Ramesh Chand Kapoor, Mr. Vishal Kapoor, Mr. Krrish Kapoor, Mr. Vinay Kapoor and Ms. Purva Kapoor.",
    ],
    photo: null,
    image: "/images/directors.jpg",
    caption: "The directors of RK Group",
  },
];

const NEXT_CHAPTER_POINTS = [
  "RK Group is preparing to enter the hospitality industry, with future ventures currently in the pipeline.",
  "The Group will also explore opportunities in new industries and business sectors as part of its long-term growth vision.",
  "Every future venture should remain connected to the values on which RK Group has been built: trust, quality, relationships and long-term value.",
];

const PhotoFrame = ({ src, alt, caption }) => (
  <figure className="max-w-sm w-full mx-auto group" data-testid="story-photo-frame">
    <div className="border border-[#1E232A]/15 bg-[#F3EEE3] p-2.5 shadow-[0_1px_0_rgba(30,35,42,0.04)]">
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.04]"
        />
      </div>
    </div>
    <figcaption className="text-center font-serif italic text-sm text-[#1E232A]/45 mt-4">{caption}</figcaption>
  </figure>
);

const MilestoneText = ({ m, right }) => (
  <div className={right ? "lg:text-right" : ""}>
    <p className="font-serif font-light text-6xl sm:text-7xl xl:text-8xl text-[#C5A059] leading-none">{m.year}</p>
    <h2 className="font-serif text-2xl sm:text-3xl text-[#1E232A] mt-5">{m.title}</h2>
    <ul className="mt-7 space-y-4">
      {m.points.map((p, i) => (
        <li
          key={i}
          className={`flex items-start gap-4 text-sm sm:text-base text-[#1E232A]/70 leading-relaxed ${
            right ? "lg:flex-row-reverse" : ""
          }`}
        >
          <span className="mt-[0.65em] w-5 h-px bg-[#C5A059] shrink-0" />
          <span>{p}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function Story() {
  const timelineRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start 0.55", "end 0.75"],
  });

  return (
    <div data-testid="story-page" className="bg-[#F8F4EB] text-[#1E232A] pt-28 sm:pt-36">
      <SEO title="Our Story"
        description="The RK Group timeline — from Sarafa Bazaar, Meerut in 1976 to RK Builders, Karol Bagh, Abu Plaza, and the next chapter in hospitality." />

      <header className="max-w-[800px] mx-auto px-5 sm:px-8 pb-16 sm:pb-24 text-center">
        <Eyebrow light>Our Story</Eyebrow>
        <h1 className="font-serif font-light text-4xl sm:text-6xl tracking-tight mt-5 leading-[1.05]">
          Built Through Work. <span className="italic text-[#C5A059]">Continued by Family.</span>
        </h1>
        <p className="text-sm sm:text-base text-[#1E232A]/55 leading-relaxed mt-8 max-w-xl mx-auto">
          The milestones of a family business house — from Sarafa Bazaar, Meerut, to what comes next.
        </p>
      </header>

      <section ref={timelineRef} className="relative max-w-[1200px] mx-auto px-5 sm:px-8" data-testid="story-timeline">
        <div className="absolute top-0 bottom-0 left-[27px] lg:left-1/2 w-px bg-[#1E232A]/15" />
        <motion.div
          style={{ scaleY: scrollYProgress }}
          className="absolute top-0 bottom-0 left-[27px] lg:left-1/2 w-px bg-[#C5A059] origin-top"
          aria-hidden="true"
        />
        {MILESTONES.map((m, i) => {
          const right = i % 2 === 0;
          return (
            <Reveal key={m.year} className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-24 py-12 lg:py-20 pl-14 lg:pl-0">
              <span className="absolute left-[27px] lg:left-1/2 top-16 lg:top-24 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-[#F8F4EB] border-2 border-[#C5A059]" />
              <div className={right ? "lg:order-1" : "lg:order-2"}>
                <MilestoneText m={m} right={right} />
              </div>
              <div className={`${right ? "lg:order-2" : "lg:order-1"} flex items-center`}>
                {m.image ? (
                  <PhotoFrame src={m.image} alt={`${m.year} — ${m.title}`} caption={m.caption} />
                ) : (
                  <div className="hidden lg:block" />
                )}
              </div>
            </Reveal>
          );
        })}

        {/* THE NEXT CHAPTER — the line opens forward */}
        <div className="relative py-20 lg:py-28 pl-14 lg:pl-0 lg:text-center" data-testid="story-next-chapter">
          <div className="absolute left-[27px] lg:left-1/2 -top-2 h-20 w-px border-l border-dashed border-[#C5A059]/70 lg:-translate-x-1/2" />
          <span className="absolute left-[27px] lg:left-1/2 top-20 -translate-x-1/2 w-5 h-5 rounded-full border-2 border-[#C5A059] bg-transparent" />
          <Reveal>
            <Eyebrow light>Looking Forward</Eyebrow>
            <h2 className="font-serif font-light text-4xl sm:text-6xl tracking-tight mt-5">
              The <span className="italic text-[#C5A059]">Next Chapter</span>
            </h2>
            <p className="font-serif italic text-xl sm:text-2xl text-[#1E232A]/60 mt-4">Hospitality &amp; Beyond</p>
            <ul className="mt-10 space-y-4 max-w-2xl lg:mx-auto text-left">
              {NEXT_CHAPTER_POINTS.map((p, i) => (
                <li key={i} className="flex items-start gap-4 text-sm sm:text-base text-[#1E232A]/70 leading-relaxed">
                  <span className="mt-[0.65em] w-5 h-px bg-[#C5A059] shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-start lg:justify-center gap-5 mt-16">
              <span className="h-px w-16 sm:w-24 bg-[#C5A059]/50" />
              <span className="w-2 h-2 rotate-45 border border-[#C5A059]" />
              <span className="h-px w-24 sm:w-40 bg-gradient-to-r from-[#C5A059]/50 to-transparent" />
            </div>
            <p className="font-serif italic text-xl sm:text-2xl lg:text-3xl text-[#1E232A]/70 leading-relaxed max-w-3xl lg:mx-auto mt-14">
              From jewellery to real estate, and now towards hospitality and beyond — the RK Group story
              continues to evolve across generations.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
